import { createSlice } from "@reduxjs/toolkit";

import {
    fetchTenderCategories,
    fetchTenderType,
    fetchVendorOptions,
    fetchTenderDetail,
    submitTender,
    fetchDashboard,
    addTenderCategory
} from "./internalTenderThunk";

const initialState = {
    loading: false,
    error: null,
    categories: [],
    types: [],
    vendorOptions: [],
    vendorLoading: false,
    vendorError: null,

    dashboard: [],
    actionItems: [],
    dashboardLoading: false,

    vendorSearch: "",
    vendorPage: 1,
    vendorHasMore: true,

    selectedVendors: [],
    detail: {
        id: null,
        title: "",
        description: "",
        status: "",
        budget: "",
        category: "",
        deadline: "",
        vendor_count: 0,
        current_step: 0,

        documents: [],
        vendors: [],
        timeline: [],
        stages: [],
        proof_history: [],

        sla: {
            status: "",
            late_days: 0,
            message: "",
        },
    },
};

const internalTenderSlice = createSlice({
    name: "internalTender",

    initialState,

    reducers: {
        clearTenderDetail(state) {
            state.detail = initialState.detail;
            state.loading = false;
            state.error = null;
        },

        setCurrentStep(state, action) {
            state.detail.current_step = action.payload;
        },

        updateTenderStatus(state, action) {
            state.detail.status = action.payload;
        },

        updateProofHistory(state, action) {
            state.detail.proof_history.unshift(action.payload);
        },

        updateSLA(state, action) {
            state.detail.sla = action.payload;
        },

        clearVendorOptions(state) {
            state.vendorOptions = [];
            state.vendorPage = 1;
            state.vendorHasMore = true;
        },

        setVendorSearch(state, action) {
            state.vendorSearch = action.payload;
        },

        addSelectedVendor(state, action) {

            const exists = state.selectedVendors.find(
                x => x.id === action.payload.id
            );

            if (!exists) {
                state.selectedVendors.push(action.payload);
            }
        },

        removeSelectedVendor(state, action) {

            state.selectedVendors =
                state.selectedVendors.filter(
                    x => x.id !== action.payload
                );
        },

        clearSelectedVendors(state) {
            state.selectedVendors = [];
        },

        setSelectedVendors: (state, action) => {
            state.selectedVendors = action.payload;
        },
    },

    extraReducers: (builder) => {
        builder

            .addCase(fetchTenderDetail.pending, (state) => {
                state.loading = true;
                state.error = null;
            })

            .addCase(fetchTenderDetail.fulfilled, (state, action) => {
                state.loading = false;

                state.detail = {
                    ...state.detail,
                    ...action.payload,
                };
            })

            .addCase(fetchTenderDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // CATEGORIES
            .addCase(fetchTenderCategories.pending, (state) => {
                state.loading = true;
            })

            .addCase(fetchTenderCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload;
            })

            .addCase(fetchTenderCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(addTenderCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(addTenderCategory.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.categories.push(action.payload);
                }
            })
            .addCase(addTenderCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // TYPES
            .addCase(fetchTenderType.pending, (state) => {
                state.loading = true;
            })

            .addCase(fetchTenderType.fulfilled, (state, action) => {
                state.loading = false;
                state.types = action.payload;
            })

            .addCase(fetchTenderType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchVendorOptions.pending, (state) => {
                state.vendorLoading = true;
            })

            .addCase(fetchVendorOptions.fulfilled, (state, action) => {
                state.vendorLoading = false;

                if (action.payload.page === 1) {
                    state.vendorOptions = action.payload.vendors;
                } else {
                    state.vendorOptions = [
                        ...state.vendorOptions,
                        ...action.payload.vendors,
                    ];
                }

                state.vendorPage = action.payload.currentPage;

                state.vendorHasMore =
                    action.payload.currentPage < action.payload.lastPage;
            })

            .addCase(fetchVendorOptions.rejected, (state, action) => {
                state.vendorLoading = false;
                state.vendorError = action.payload;
            })

            .addCase(fetchDashboard.pending, (state) => {
                state.dashboardLoading = true;
            })
            .addCase(fetchDashboard.fulfilled, (state, action) => {
                state.dashboardLoading = false;
                state.dashboard = action.payload.summary;
                state.actionItems = action.payload.action_items;
            })
            .addCase(fetchDashboard.rejected, (state) => {
                state.dashboardLoading = false;
            })

            .addCase(submitTender.pending, (state) => {
                state.loading = true;
            })

            .addCase(submitTender.fulfilled, (state, action) => {
                state.loading = false;

                // update status lokal agar UI langsung berubah
                if (state.detail) {
                    state.detail.status = 1;
                    state.detail.current_step = 1;
                }
            })

            .addCase(submitTender.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearTenderDetail,
    setCurrentStep,
    updateTenderStatus,
    updateProofHistory,
    updateSLA,
    clearVendorOptions,
    setVendorSearch,
    addSelectedVendor,
    removeSelectedVendor,
    clearSelectedVendors,
    setSelectedVendors,
} = internalTenderSlice.actions;

export default internalTenderSlice.reducer;